import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { sendGtEvent } from 'apps/auth/analytics/analytics';
import { Button } from 'apps/auth/components/common/Button/Button';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { MainColumn } from 'apps/auth/components/common/Layout/MainColumn/MainColumn';
import { BlockSubheader, FeaturesBlock, FeaturesList, FeaturesListItem, IntroBlock, IntroBlockImg } from 'apps/auth/components/common/Layout/MainColumn/styles';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import ResidenceAddressUpdatePopup from 'apps/auth/components/common/popups/ResidenceAddressUpdatePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import BasePageWithLeftMenu from 'apps/auth/components/utils/BasePageWithLeftMenu';
import { ONBOARDING_COMPLETED, REQUEST_MONEY_BUTTON_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { REQUEST } from 'apps/auth/constants/Constants';
import { IS_NEW_USER, SHOW_MODAL_PROFILE_UPDATE_BLOCK, SHOW_MODAL_TERMS_UPDATE_BLOCK } from 'apps/auth/constants/ExtraNames';
import { GA4_SIGN_UP, GT_LOGIN_SUCCESS, GT_REG_SUCCESS } from 'apps/auth/constants/GoogleAnalyticsEvents';
import { GT_USER_ID } from 'apps/auth/constants/GoogleAnalyticsParamNames';
import { AUTH_REQUEST_CREATE, AUTH_REQUEST_MAIN_LINK, URL_VIDEOS, URL_WELCOME_SCREEN_IMG } from 'apps/auth/constants/UrlConstants';
import { useExtra } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { usePrefersReducedMotion } from 'apps/auth/hooks/usePrefersReducedMotion';

import RequestHistory from './RequestHistory';
import ProfileUpdatePopup from '../../notificationPopup/ProfileUpdatePopup';
import TermsUpdatePopup from '../../notificationPopup/TermsUpdatePopup';
import HistorySpinner from '../HistorySpinner';
import Notification from '../Notification';

const RequestMain = (props) => {
    const location = useLocation();
    const popupRef = useRef(null);
    const { residenceAddressMustBeUpdated } = useExtra();
    const { theme } = useThemeDetector();
    const prefersReducedMotion = usePrefersReducedMotion();
    const heroTheme = theme === 'dark' ? 'dark' : 'light';
    const heroPoster = `${URL_WELCOME_SCREEN_IMG}/welcome-placeholder-blue-${heroTheme}.webp`;
    // TODO: temporary — always show the static logo until the updated video logo is delivered.
    const isImageLogo = true;

    useEffect(() => {
        if (location.search === '?login=completed') {
            sendGtEvent(GT_LOGIN_SUCCESS, { [GT_USER_ID]: props.ballId });
        } else if (location.search === '?registration=completed') {
            sendGtEvent(GT_REG_SUCCESS, { [GT_USER_ID]: props.ballId });
        }
    }, []);

    useEffect(() => {
        if (props.isNewUser) {
            amplitude.getInstance().logEvent(ONBOARDING_COMPLETED);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: GA4_SIGN_UP });
            props.updateExtraByName(IS_NEW_USER, false);
        }
    }, [props.isNewUser]);

    const isShowResults = () => {
        return props.records && props.records.bills && props.records.bills.length > 0;
    };

    const onRequestCreate = (e) => {
        amplitude.getInstance().logEvent(REQUEST_MONEY_BUTTON_CLICKED);
        let popup = null;
        if (props.show_modal_profile_update_block) {
            popup = <ProfileUpdatePopup popupRef={popupRef} redirectUrl={AUTH_REQUEST_MAIN_LINK} />;
        } else if (props.show_modal_terms_update_block) {
            popup = <TermsUpdatePopup popupRef={popupRef} redirectUrl={AUTH_REQUEST_MAIN_LINK} />;
        } else if (residenceAddressMustBeUpdated) {
            popup = <ResidenceAddressUpdatePopup popupRef={props.popupRef} />;
        }
        if (popup) {
            e.preventDefault();
            e.stopPropagation();
            popupRef.current?.open(popup);
        } else {
            props.toggleSpinner(true);
        }
    };

    let featuresArray = [
        props.featuresTitleSection1,
        props.featuresTitleSection2,
        props.featuresTitleSection3,
        props.featuresTitleSection4,
        props.featuresTitleSection5,
    ].filter(function (e) {
        return e;
    });

    let featuresList = (
        <FeaturesList>
            {featuresArray.map((item, i) => {
                return (
                    <FeaturesListItem key={item + i}>
                        <Icon name="check" color="primary" rightIndent="16px" />
                        <span>{item}</span>
                    </FeaturesListItem>
                );
            })}
        </FeaturesList>
    );
    let content;
    if (isShowResults()) {
        content = (
            <>
                <HeaderPanel title={props.headerTitle} />
                {!props.loading && <RequestHistory />}
                {props.isShowCustomSpinner && <HistorySpinner />}
            </>
        );
    } else {
        content = (
            <>
                <IntroBlock>
                    <IntroBlockImg>
                        {isImageLogo || prefersReducedMotion ? (
                            <img src={heroPoster} alt="Paysend" />
                        ) : (
                            <video autoPlay muted playsInline preload="auto" poster={heroPoster} aria-label="Paysend">
                                <source src={`${URL_VIDEOS}/paysend-${heroTheme}-hero.mp4`} type="video/mp4" />
                            </video>
                        )}
                    </IntroBlockImg>
                    {!props.canCreateBill ? (
                        <Text variant="paragraph">{props.waitingListTitle}</Text>
                    ) : (
                        <Text variant="paragraph">
                            {props.historyTitlePart1} {props.historyTitlePart2}
                        </Text>
                    )}
                </IntroBlock>
                {featuresArray.length > 0 && (
                    <>
                        <Separator />
                        <FeaturesBlock>
                            <BlockSubheader>{props.featuresTitle}</BlockSubheader>
                            {featuresList}
                        </FeaturesBlock>
                    </>
                )}
                {props.canCreateBill && (
                    <FixedBottomPanel fullwidth isPressedAboveMenu isRightAligned>
                        <Button
                            type="link"
                            raised={true}
                            href={AUTH_REQUEST_CREATE}
                            caption={props.newRequest}
                            onClick={onRequestCreate}
                            id="request_new_request_button_id"
                        />
                    </FixedBottomPanel>
                )}
            </>
        );
    }
    return (
        <>
            <MainColumn>
                <Notification />
                {content}
            </MainColumn>
            <PopupNew ref={popupRef} />
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        lang: state.language.lang,
        country: state.app.country,
        isShowCustomSpinner: state.app.isShowCustomSpinner,
        headerTitle: state.messages.pageMessages.headerTitle,
        waitingListTitle: state.messages.pageMessages.waitingListTitle,
        historyTitlePart1: state.messages.pageMessages.historyTitlePart1,
        historyTitlePart2: state.messages.pageMessages.historyTitlePart2,
        featuresTitle: state.messages.pageMessages.featuresTitle,
        featuresTitleSection1: state.messages.pageMessages.featuresTitleSection1,
        featuresTitleSection2: state.messages.pageMessages.featuresTitleSection2,
        featuresTitleSection3: state.messages.pageMessages.featuresTitleSection3,
        featuresTitleSection4: state.messages.pageMessages.featuresTitleSection4,
        featuresTitleSection5: state.messages.pageMessages.featuresTitleSection5,
        newRequest: state.messages.pageMessages.newRequest,
        messages: state.messages.pageMessages,
        records: state.history.billHistory,
        ballId: state.auth.ballId,
        canCreateBill: state.extra.canCreateBill,
        userCountry: state.extra.userCountry,
        isNewUser: Boolean(state.extra[IS_NEW_USER]),
        show_modal_profile_update_block: state.extra?.[SHOW_MODAL_PROFILE_UPDATE_BLOCK],
        show_modal_terms_update_block: state.extra?.[SHOW_MODAL_TERMS_UPDATE_BLOCK],
    };
};
export default connect(mapStateToProps, {
    toggleSpinner,
    updateExtraByName,
})(BasePageWithLeftMenu(RequestMain, REQUEST));
